import './ShareMain.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Header } from '../Header/Header';
import {
    ISharingInformation, ISharingLinkSettings, ISharingItemInformation, SharingLinkKind, ICustomLabeledAudienceConfig,
    ClientId, ShareType, SharingAudience, AccessStatus, OutlookAvailability, IEngagementExtraData, Mode
} from '../../interfaces/SharingInterfaces';
import { SendLink } from '../SendLink/SendLink';
import { ShareTargets } from './ShareTargets/ShareTargets';
import { ShareHint } from '../ShareHint/ShareHint';
import { ShareViewState } from '../Share/Share';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import stringsResx from '../Share.resx';
import AttachAsCopyHelper from '../../utilities/AttachAsCopyHelper';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { ActivityIndicator } from '../ActivityIndicator/ActivityIndicator';
import * as PeoplePickerHelper from '../../utilities/PeoplePickerHelper';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import * as EngagementHelper from '../../utilities/EngagementHelper';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import { isLoadingData } from '../../utilities/ShareHelper';
import { createRef } from 'office-ui-fabric-react/lib/Utilities';

const MAXIMUM_MESSAGE_LENGTH = 500;
const MAXIMUM_SUBJECT_LENGTH = 100;

export interface IShareMainProps {
    clientId: ClientId;
    scenarioId: string;
    companyName: string;
    currentSettings: ISharingLinkSettings;
    item: ISharingItemInformation;
    onShareHintClicked: () => void;
    onSendLinkClicked: (message: string, subject?: string) => void;
    onShowPermissionsListClicked: () => void;
    onPolicyClick: () => void;
    sharingInformation: ISharingInformation;
    onSelectedPeopleChange: (items: Array<any>) => void;
    groupsMemberCount: number;
    onViewPolicyTipClicked: () => void;
    linkRecipients: Array<IPerson>;
    permissionsMap: { [index: string]: AccessStatus };
    messageText: string;
    subjectText?: string;
    onSendLinkMessageChange: (messageText: string) => void;
    onSendLinkSubjectChange?: (subjectText: string) => void;
    onShareTargetClicked: (shareType: ShareType) => void;
    onShareTargetsRendered: (shareTargets: Array<ShareType>) => void;
    hideShareDialogOverflowButton?: boolean; // If true, hide the more button from header.
    createPeopleSharingLinks?: boolean;
    outlookAvailability: OutlookAvailability;
    engagementExtraData: IEngagementExtraData;
    enableShimmer: boolean;
    isLoadingDefaultRecipients: boolean;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
    closeButtonOnFocus?: () => void;
    closeButtonOnBlur?: () => void;
}

export interface IShareMainState {
    isAttachAsCopyContextualMenuVisible: boolean;
    shareType: ShareType;
    showActivityIndicator: boolean;
    peoplePickerErrorMessage: JSX.Element;
}

export class ShareMain extends React.Component<IShareMainProps, IShareMainState> {
    private _headerControl = createRef<Header>();
    private _shareClickTime: number;

    static contextTypes = {
        shareClickTime: PropTypes.number
    };

    constructor(props: IShareMainProps, context: any) {
        super(props);
        this._shareClickTime = context.shareClickTime;

        this.state = {
            isAttachAsCopyContextualMenuVisible: false,
            shareType: ShareType.share,
            showActivityIndicator: false,
            peoplePickerErrorMessage: null
        };
    }

    public componentDidMount() {
        // Attempt to notify host that UI is really ready.
        ShareCommunicationManager.pageFinishedLoading(this._shareClickTime, this.props.clientId, Mode.share, this.props.scenarioId);

        // Re-evaluate selected recipients.
        this._updatePeoplePickerError(this.props);

        if (ClientIdHelper.isMobile(this.props.clientId) && this._headerControl.current) {
            this._headerControl.current.focusCloseButton();
            this.props.closeButtonOnFocus();
        }
    }

    public componentWillReceiveProps(newProps: IShareMainProps) {
        // Re-evaluate selected recipients.
        this._updatePeoplePickerError(newProps);
    }

    public render(): React.ReactElement<{}> {
        const blockerClass: string = this.state.showActivityIndicator ? ' blocker' : '';
        const props = this.props;

        const label = this.state.showActivityIndicator ? this._getActivityMessage() : '';
        const hideShareDialogOverflowButton = this.props.hideShareDialogOverflowButton || false;

        const shareFooter: JSX.Element = this._renderFooter();
        const shareBodyClass = shareFooter ? { className: 'od-ShareMain-section-body' } : null;

        return (
            <div className={ 'od-ShareMain' + blockerClass }>
                <Header
                    ctaLabel={ stringsResx.sendButtonLabel }
                    clientId={ props.clientId }
                    currentSettings={ props.currentSettings }
                    errorMessage={ this.state.peoplePickerErrorMessage }
                    item={ props.item }
                    linkRecipients={ props.linkRecipients }
                    permissionsMap={ props.permissionsMap }
                    onManageExistingAccessClick={ this.props.onShowPermissionsListClicked }
                    onSendLinkClick={ this._onSendLinkClicked }
                    messageText={ this.props.messageText }
                    subjectText={ this.props.subjectText }
                    viewState={ ShareViewState.default }
                    sharingInformation={ props.sharingInformation }
                    hideShareDialogOverflowButton={ hideShareDialogOverflowButton }
                    maximumMessageLength={ MAXIMUM_MESSAGE_LENGTH }
                    maximumSubjectLength={ MAXIMUM_SUBJECT_LENGTH }
                    ref={ this._headerControl }
                    closeButtonOnBlur={ this.props.closeButtonOnBlur }
                />
                <div { ...shareBodyClass }>
                    <div className='od-ShareMain-section full-bleed'>
                        <ShareHint
                            clientId={ props.clientId }
                            companyName={ props.companyName }
                            currentSettings={ props.currentSettings }
                            onShareHintClick={ props.onShareHintClicked }
                            sharingInformation={ props.sharingInformation }
                            shouldTakeFocus={ !props.enableShimmer }
                            linkRecipients={ null }
                            customLabeledAudienceConfig={ props.customLabeledAudienceConfig }
                        />
                    </div>
                    { this._renderSendLink() }
                    { this._renderTargets() }
                </div>
                { shareFooter }
                { this._renderActivityIndicator() }

                <span role='alert' className='od-Share-screenReaderOnly'>{ label }</span>
            </div>
        );
    }

    private _renderTargets(): JSX.Element {
        return (
            <div className='od-ShareMain-targets'>
                <ShareTargets
                    clientId={ this.props.clientId }
                    currentSettings={ this.props.currentSettings }
                    onShareTargetClicked={ this._onShareTargetClicked }
                    onShareTargetsRendered={ this.props.onShareTargetsRendered }
                    outlookAvailability={ this.props.outlookAvailability }
                    sharingInformation={ this.props.sharingInformation }
                />
            </div>
        );
    }

    private _renderFooter(): JSX.Element {
        const props = this.props;
        const clientId = props.clientId;

        if (ClientIdHelper.isOfficeProduct(clientId)) {
            const attachmentOptions = AttachAsCopyHelper.getAttachAsCopyOptions(clientId, this.props.engagementExtraData);

            if (attachmentOptions.length > 0) {
                return (
                    <div>
                        <DefaultButton
                            className='od-ShareMain-footer'
                            text={ stringsResx.attachACopyInstead }
                            iconProps={ {
                                iconName: 'Copy'
                            } }
                            menuProps={ {
                                shouldFocusOnMount: true,
                                items: attachmentOptions
                            } }
                        />
                    </div>
                );
            }
        } else if ((clientId === ClientId.ngsc || ClientIdHelper.isMobile(clientId)) && this._isSendCopyEnabled()) {
            return (
                <div>
                    <button
                        className='od-ShareMain-footer'
                        onClick={ this._onAttachAsCopyClicked }
                    >
                        <div className='od-ShareMain-attachIcon'>
                            <Icon iconName='Copy' />
                        </div>
                        <span>{ stringsResx.attachACopyInstead }</span>
                    </button>
                </div>
            );
        }
    }

    private _renderSendLink(): JSX.Element {
        if (!this.props.sharingInformation.blockPeoplePickerAndSharing ||
            isLoadingData(this.props.sharingInformation)) {

            const props = this.props;

            return (
                <div className='od-ShareMain-section'>
                    <SendLink
                        ctaLabel={ stringsResx.sendButtonLabel }
                        showTextArea={ true }
                        sharingInformation={ props.sharingInformation }
                        onSendLinkClicked={ this._onSendLinkClicked }
                        currentSettings={ props.currentSettings }
                        onSelectedPeopleChange={ this._onSelectedPeopleChange }
                        groupsMemberCount={ props.groupsMemberCount }
                        onViewPolicyTipClicked={ props.onViewPolicyTipClicked }
                        linkRecipients={ props.linkRecipients }
                        permissionsMap={ props.permissionsMap }
                        messageText={ props.messageText }
                        subjectText={ props.subjectText }
                        onSendLinkMessageChange={ props.onSendLinkMessageChange }
                        onSendLinkSubjectChange={ props.onSendLinkSubjectChange }
                        createPeopleSharingLinks={ props.createPeopleSharingLinks }
                        errorMessage={ this.state.peoplePickerErrorMessage }
                        clientId={ props.clientId }
                        showSendButton={ true }
                        maximumMessageLength={ MAXIMUM_MESSAGE_LENGTH }
                        maximumSubjectLength={ MAXIMUM_SUBJECT_LENGTH }
                        isLoadingDefaultRecipients={ props.isLoadingDefaultRecipients }
                        onManageExistingAccessClick={ this.props.onShowPermissionsListClicked }
                        engagementExtraData={ this.props.engagementExtraData }
                    />
                </div>
            );
        }
    }

    private _renderActivityIndicator(): React.ReactElement<{}> {
        if (this.state.showActivityIndicator) {
            return (
                <ActivityIndicator message={ this._getActivityMessage() } />
            );
        }
    }

    @autobind
    private _onSelectedPeopleChange(items: Array<IPerson>) {
        const props = this.props;

        // Get people picker error based on resolved items in the picker.
        const peoplePickerError = PeoplePickerHelper.renderPickerError({
            selectedItems: items,
            sharingLinkKind: props.currentSettings.sharingLinkKind,
            canAddExternalPrincipal: props.sharingInformation.canAddExternalPrincipal,
            sharingInformation: props.sharingInformation,
            viewPolicyTipCallback: props.onViewPolicyTipClicked,
            permissionsMap: props.permissionsMap
        });
        if (peoplePickerError) {
            const sharingLinkKind = props.currentSettings.sharingLinkKind;
            const isCompanyShareableLink = sharingLinkKind === SharingLinkKind.organizationEdit || sharingLinkKind === SharingLinkKind.organizationView;
            let hasExternalSharingCslError = false;
            for (const item of items) {
                if (item.isExternal && isCompanyShareableLink) {
                    hasExternalSharingCslError = true;
                    break;
                }
            }
            if (hasExternalSharingCslError) {
                EngagementHelper.externalSharingCslError(props.engagementExtraData);
            }
        }

        this.setState({
            ...this.state,
            peoplePickerErrorMessage: peoplePickerError
        }, () => {
            props.onSelectedPeopleChange(items);
        });
    }

    @autobind
    private _onShareTargetClicked(shareType: ShareType): void {
        if (!this._isPeoplePickerValid(shareType) && (this.props.currentSettings.audience === SharingAudience.specificPeopleFlex || this.props.currentSettings.audience === SharingAudience.specificPeople || this.props.linkRecipients.length > 0)) {
            return;
        }

        this.setState({
            ...this.state,
            showActivityIndicator: true,
            shareType: shareType
        }, () => {
            this.props.onShareTargetClicked(shareType);
        });
    }

    @autobind
    private _onSendLinkClicked(message: string, subject?: string): void {
        if (!this._isPeoplePickerValid(ShareType.share)) {
            return;
        }

        this.setState({
            ...this.state,
            showActivityIndicator: true,
            shareType: ShareType.share
        }, () => {
            this.props.onSendLinkClicked(message, subject);
        });
    }

    @autobind
    private _onAttachAsCopyClicked() {
        const clientId = this.props.clientId;
        EngagementHelper.sendACopyClicked(this.props.engagementExtraData);

        if (ClientIdHelper.isOfficeProduct(clientId)) {
            const showContextualMenu = !this.state.isAttachAsCopyContextualMenuVisible;
            this.setState({
                ...this.state,
                isAttachAsCopyContextualMenuVisible: showContextualMenu
            }, () => { });
        } else if (clientId === ClientId.ngsc || ClientIdHelper.isMobile(clientId)) {
            try {
                const externalJavaScript: any = window.external;
                externalJavaScript.SendCopy();
            } catch (error) {
                // Nothing.
            }
        }
    }

    private _getActivityMessage(): string {
        const strings = stringsResx;

        switch (this.state.shareType) {
            case ShareType.share:
                return strings.activityMessageSendingMail;
            case ShareType.copy:
            case ShareType.outlook:
            case ShareType.nonOutlook:
            case ShareType.teams:
            case ShareType.moreApps:
                return strings.activityMessageCreatingLink;
            default:
                return strings.activityMessageCreatingLink;
        }
    }

    private _isSendCopyEnabled(): boolean {
        try {
            const externalJavaScript: any = window.external;
            return externalJavaScript.IsSendCopyEnabled();
        } catch (error) {
            // If "IsSendCopyEnabled" is not implemented, return false.
            return false;
        }
    }

    private _isPeoplePickerValid(shareType: ShareType): boolean {
        if (this.state.peoplePickerErrorMessage) {
            return;
        }

        /**
         * If we're creating a "Specific people" link for any reason
         * except for sending it, we need to ensure that there's not
         * a mix of internal and external users.
         */
        if (shareType !== ShareType.share && !this.props.createPeopleSharingLinks && this.props.currentSettings.audience === SharingAudience.specificPeople) {
            let hasExternalRecipient = false;
            let hasInternalRecipient = false;

            for (const recipient of this.props.linkRecipients) {
                if (!hasExternalRecipient) {
                    hasExternalRecipient = recipient.isExternal && !recipient.providerName;
                }

                if (!hasInternalRecipient) {
                    hasInternalRecipient = !recipient.isExternal ||
                        (recipient.isExternal && !!recipient.providerName);
                }
            }

            if (hasExternalRecipient && hasInternalRecipient) {
                this.setState({
                    ...this.state,
                    peoplePickerErrorMessage: <span>{ stringsResx.mixedRecipientErrorMessage }</span>
                });
                return false;
            }
        }

        const peoplePickerInput = document.querySelector('.od-Share-PeoplePicker .ms-BasePicker-input') as HTMLInputElement;

        const requiresRecipients = this.props.currentSettings.audience === SharingAudience.specificPeopleFlex ||
            this.props.currentSettings.audience === SharingAudience.specificPeople ||
            shareType === ShareType.share;

        /**
         * - Show error message if there's unresolved text in picker.
         * - Show error message if there are no recipients (for PSLs).
         **/
        if (peoplePickerInput.value) {
            this.setState({
                ...this.state,
                peoplePickerErrorMessage: <span>{ stringsResx.unresolvedTextError }</span>
            });
            return false;
        } else if (this.props.linkRecipients.length === 0 && requiresRecipients) {
            this.setState({
                ...this.state,
                peoplePickerErrorMessage: <span>{ stringsResx.recipientsRequiredError }</span>
            });
            return false;
        }

        return true;
    }

    private _updatePeoplePickerError(props: IShareMainProps) {
        if (props.linkRecipients) {
            const peoplePickerError = PeoplePickerHelper.renderPickerError({
                selectedItems: props.linkRecipients,
                sharingLinkKind: props.currentSettings.sharingLinkKind,
                canAddExternalPrincipal: props.sharingInformation.canAddExternalPrincipal,
                sharingInformation: props.sharingInformation,
                viewPolicyTipCallback: props.onViewPolicyTipClicked,
                permissionsMap: props.permissionsMap
            });

            this.setState({
                ...this.state,
                peoplePickerErrorMessage: peoplePickerError
            });
        }
    }
}
